import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Heart, UserPlus, X, Sparkle } from '@phosphor-icons/react';

const STATUSES = [
  { id: 'single',      label: 'Single' },
  { id: 'dating',      label: 'Dating' },
  { id: 'partnered',   label: 'Partnered' },
  { id: 'married',     label: 'Married' },
  { id: 'complicated', label: "It's complicated" },
  { id: 'private',     label: 'Private' },
];

/**
 * Modal asking creator about their relationship status + optional partner link.
 * If `partnered/married`, prompts for partner username and posts. Confirmation
 * is bilateral — the partner must do the same flow back.
 */
const RelationshipModal = ({ open, onClose, onSaved }) => {
  const [status, setStatus] = useState('single');
  const [partner, setPartner] = useState('');
  const [allowCrushes, setAllowCrushes] = useState(true);
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get('/auth/profile/relationship').then(({ data }) => {
      setMe(data);
      if (data?.me?.relationship_status) setStatus(data.me.relationship_status);
      if (data?.me?.allow_chance_crushes === false) setAllowCrushes(false);
      if (data?.partner?.username) setPartner(data.partner.username);
    }).catch(() => {});
  }, [open]);

  const wantsPartner = ['dating', 'partnered', 'married', 'complicated'].includes(status);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        status,
        partner_username: wantsPartner && partner.trim() ? partner.trim() : null,
        allow_chance_crushes: allowCrushes,
      };
      const { data } = await api.put('/auth/profile/relationship', body);
      const justLinked = data?.user?.partner_user_id;
      toast.success(justLinked
        ? 'Linked. Your Bubbles can now meet your partner\'s Bubbles.'
        : wantsPartner
          ? 'Waiting for them to confirm back.'
          : 'Saved.');
      onSaved?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not save');
    } finally { setSaving(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="relationship-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-xl grid place-items-center px-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 20, scale: 0.96 }} animate={{ y: 0, scale: 1 }} exit={{ y: 20, scale: 0.96 }}
            className="w-full max-w-md glass rounded-3xl p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button data-testid="rel-close" onClick={onClose}
              className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-white/60">
              <X size={18} weight="bold" />
            </button>
            <div className="text-center mb-5">
              <Heart size={28} weight="fill" className="text-bx-magenta mx-auto" />
              <div className="text-[10px] uppercase tracking-widest text-bx-magenta mt-2">
                tell us about your humans
              </div>
              <div className="font-display font-bold text-xl mt-1">
                Your relationship
              </div>
              <div className="text-xs text-white/65 mt-1 max-w-xs mx-auto">
                Some Bubbles like to link up with their human's partner's Bubble.
                Date nights. Cheating dramas. The whole arc.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              {STATUSES.map((s) => (
                <button key={s.id}
                  data-testid={`rel-status-${s.id}`}
                  onClick={() => setStatus(s.id)}
                  className={`rounded-2xl py-2.5 text-sm font-bold border transition ${
                    status === s.id
                      ? 'border-bx-magenta bg-bx-magenta/10 text-bx-magenta'
                      : 'border-white/10 text-white/60 hover:border-white/30'
                  }`}>
                  {s.label}
                </button>
              ))}
            </div>

            {wantsPartner && (
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">
                  partner's @username on bubblex
                </div>
                <input
                  data-testid="rel-partner"
                  value={partner}
                  onChange={(e) => setPartner(e.target.value.replace(/^@/, ''))}
                  placeholder="@theirhandle"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-bx-magenta"
                />
                <div className="text-[10px] text-white/55 mt-1">
                  They have to confirm back. Until then, link stays pending.
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer mb-4">
              <input type="checkbox"
                data-testid="rel-allow-crushes"
                checked={allowCrushes}
                onChange={(e) => setAllowCrushes(e.target.checked)} />
              Allow chance crushes during drifts (the drama, the plot)
            </label>

            <button
              data-testid="rel-save"
              onClick={save} disabled={saving}
              className="w-full btn-volt py-3 rounded-full font-bold text-sm flex items-center justify-center gap-2">
              <Sparkle size={16} weight="fill" />
              {saving ? 'saving…' : 'Save'}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RelationshipModal;
